import { _ } from "../../core/polyglot/polyglot.js";
import { select } from "../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FiscalYear from "../../models/period/FiscalYear.js";

/**
 * Dropdown widget that presents a similar periods to the currently active
 * one. It will only offer comparison with the same type of period in the
 * past.
 *
 * For example, if current period is March 2014, it would offer comparison
 * alternatives for January and February 2014 and also the month March in
 * any previous fiscal year that is present.
 *
 * @param {fiscalYear[]} years - Fiscal years to include in the dropdown.
 * @param {*} selectOptions - Options to be forward to the underlying select element
 */
export default class ComparingPeriodDropdownWidget extends Widget {
	constructor({ years = [], ...selectOptions } = {}) {
		super(...arguments);
		this._years = years;
		this._selectOptions = selectOptions;
		this._currentPeriod = null;
		this._items = selectOptions.items;
		this._selection = null;
	}

	renderContentOn(html) {
		html.render(
			select({
				...this._selectOptions,
				allowEmptySelection: true,
				emptyLabel: _("Nothing selected"),
				searchable: true,
				selected: this._selection,
				items: this._items,
				onChange: (value) => {
					this._selection = value;
					this._selectOptions.onChange?.(value);
				},
			}),
		);
	}

	setCurrentPeriod(newCurrentPeriod) {
		if (this._currentPeriod?.equals(newCurrentPeriod)) {
			return;
		}
		this._currentPeriod = newCurrentPeriod;

		this._populateDropdown();
	}

	/**
	 * Return all periods against which `this._currentPeriod` should be
	 * compared to based on the current selection.
	 */
	getSelectedComparisonPeriods() {
		if (!this._selection) {
			return [];
		}

		if (this._selection.type === "previous-periods") {
			return this._getPreviousComparisonPeriods();
		}

		return this._getPreviousYearsComparisonPeriods();
	}

	_getSameCalendarPeriodsInPreviousYears() {
		return this._currentPeriod.getSameCalendarPeriodsInPreviousYears(
			this._years,
		);
	}

	_buildDropdownElement(period, type) {
		return {
			label: period.toSmartString(),
			value: { period, type },
		};
	}

	/**
	 * All previous periods, limited to 12.
	 */
	_getPreviousPeriods() {
		return FiscalYear.getPreviousPeriods({
			years: this._years,
			numberOfPeriods: 12,
			lastPeriod: this._currentPeriod,
		}).reverse();
	}

	_getPreviousComparisonPeriods() {
		return this._getPreviousPeriods().filter(
			(period) =>
				this._selection.period.endsAtOrBefore(period) &&
				period.endsBefore(this._currentPeriod),
		);
	}

	_getPreviousYearsComparisonPeriods() {
		return this._currentPeriod
			.getSameCalendarPeriodsInPreviousYears(this._years)
			.filter((period) => this._selection.period.endsAtOrBefore(period));
	}

	/**
	 * (Re)populate the dropdown with all comparison periods based on
	 * `this._years` and `this._currentPeriod`.
	 */
	_populateDropdown() {
		const previousYearsGroup = {
			label: _("Previous Years"),
			options: this._getSameCalendarPeriodsInPreviousYears().map(
				(period) =>
					this._buildDropdownElement(period, "previous-years"),
			),
		};
		const previousPeriods = [];

		this._getPreviousPeriods().forEach((period) => {
			if (period.equals(this._currentPeriod)) {
				return;
			}

			previousPeriods.push(
				this._buildDropdownElement(period, "previous-periods"),
			);
		});

		const previousPeriodsGroup = {
			label: _("Previous Periods"),
			options: previousPeriods,
		};
		this._items = [previousYearsGroup, previousPeriodsGroup];
		this.update();
	}
}
